import React from "react";
import { useTranslation } from "react-i18next";
import { Divider, Typography } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import { PayLinkQuote } from "hooks/usePayLinkSession";

interface PayLinkSummaryProps {
  quote: PayLinkQuote;
  currency: string;
}

const FIGURE_SX = { fontVariantNumeric: "tabular-nums" };

const PayLinkSummary: React.FC<PayLinkSummaryProps> = ({ quote, currency }) => {
  const { t } = useTranslation();

  return (
    <Stack sx={{ gap: "var(--space-2)" }}>
      <SummaryRow
        label={t("Subtotal")}
        value={formatCashFull(quote.intendedAmount, currency)}
      />

      {quote.discountAmount > 0 && (
        <SummaryRow
          label={t("Bank payment discount")}
          value={`-${formatCashFull(quote.discountAmount, currency)}`}
        />
      )}

      {quote.tipAmount > 0 && (
        <SummaryRow
          label={t("Tip")}
          value={formatCashFull(quote.tipAmount, currency)}
        />
      )}

      {quote.feeCoverAmount > 0 && (
        <SummaryRow
          label={t("Processing fee covered")}
          value={formatCashFull(quote.feeCoverAmount, currency)}
        />
      )}

      <Divider />

      <Inline justify="between">
        <Typography variant="subtitle2">{t("Total")}</Typography>
        <Typography variant="subtitle2" sx={FIGURE_SX}>
          {formatCashFull(quote.totalToCharge, currency)}
        </Typography>
      </Inline>
    </Stack>
  );
};

const SummaryRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Inline justify="between">
    <Typography variant="body2" sx={{ color: "var(--color-text-secondary)" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={FIGURE_SX}>
      {value}
    </Typography>
  </Inline>
);

export default PayLinkSummary;
